"use client";

import React, { useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ProductItem({
  product,
  index
}: {
  product: any;
  index: number;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const categoryName = product.categories && product.categories.length > 0 ? product.categories[0].name : "LUSH";
  const hoverImage = product.gallery_images?.[0] || product.featured_image_url;
  return <div data-index={index} className="group relative py-8 first:pt-0 last:pb-0" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <div className="flex items-center gap-6">
        <div className="relative size-24 overflow-hidden rounded-lg bg-muted shrink-0">
          <AnimatePresence mode="wait">
            <motion.img key={isHovered ? "hover" : "primary"} src={isHovered ? hoverImage : product.featured_image_url} alt={product.title} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.4
          }} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
          </AnimatePresence>
        </div>

        <div className="flex-1 flex flex-col">
          <span className="font-default text-[11px] tracking-[0.2em] text-muted-foreground uppercase mb-1">
            {categoryName}
          </span>
          <Link to={product.link} className="block">
            <h3 className="font-default font-medium text-xl text-foreground hover:text-accent transition-colors">
              {product.title}
            </h3>
          </Link>
          <span className="font-default font-medium text-xl text-foreground mt-1">
            ${typeof product.price === "number" ? product.price.toFixed(2) : product.price}
          </span>
        </div>

        <div className="shrink-0">
          <AddToCartButton productId={product.id} variant="link" className="w-full" buttonClassName="bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 px-4 py-2 text-sm" text="Add" showQuantity={false} />
        </div>
      </div>
    </div>;
}
function BestsellersConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 animate-pulse">
        <div className="lg:col-span-7 aspect-[4/5] bg-muted rounded-lg" />
        <div className="lg:col-span-5 space-y-8">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-muted rounded-lg" />)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="py-12 text-center">
        <p className="text-muted-foreground mb-4">Unable to load our latest arrivals.</p>
        <Button variant="outline" onClick={refetch}>Retry</Button>
      </div>;
  }
  if (products.length === 0) return null;
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
      {}
      <div className="lg:col-span-7 relative group overflow-hidden rounded-lg">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/editorial_bestsellers.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/editorial_bestsellers.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/editorial_bestsellers.webp-wvc-sizes" alt="Editorial Fashion" className="w-full aspect-[4/5] object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500" />
      </div>

      {}
      <div className="lg:col-span-5">
        {}
        <div className="hidden lg:flex flex-col">
          {products.map((product, i) => <React.Fragment key={product.id}>
              <ProductItem product={product} index={i} />
              {i < products.length - 1 && <div className="h-px w-full bg-accent/20 my-2" />}
            </React.Fragment>)}
          <div className="mt-12">
            <Link to="/ShopPage" className="group inline-flex items-center gap-2 font-default text-xs tracking-[0.2em] uppercase text-foreground border-b border-foreground/40 pb-2 w-fit hover:border-foreground transition-colors">
              <span>View All Bestsellers</span>
              <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {}
        <div className="lg:hidden">
          <div className="flex overflow-x-auto pb-8 gap-6 snap-x no-scrollbar">
            {products.map((product, i) => <div key={product.id} data-index={i} className="min-w-[280px] snap-start">
                <article className="flex flex-col gap-4">
                  <Link to={product.link} className="relative aspect-square overflow-hidden rounded-lg bg-muted">
                    <img src={product.featured_image_url} alt={product.title} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                  </Link>
                  <div>
                    <span className="font-default text-[10px] tracking-widest text-muted-foreground uppercase">
                      {product.categories?.[0]?.name || "LUSH"}
                    </span>
                    <h3 className="font-default font-medium text-lg mt-1">{product.title}</h3>
                    <p className="font-default font-medium text-lg mt-1">
                      ${typeof product.price === "number" ? product.price.toFixed(2) : product.price}
                    </p>
                    <div className="mt-4">
                      <AddToCartButton productId={product.id} variant="default" className="w-full" buttonClassName="bg-primary text-primary-foreground rounded-lg w-full" text="Add to Bag" showQuantity={false} />
                    </div>
                  </div>
                </article>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
export default function Bestsellers() {
  const wp_query_params = {
    per_page: 4,
    post_type: "product"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="2202" id="bestsellersgrid" className="bg-background text-foreground py-24 md:py-32 lg:py-40">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="mb-16 lg:mb-24">
          <p className="font-default text-xs tracking-[0.25em] text-muted-foreground uppercase mb-6">
            02 — The Collection
          </p>
          <h2 className="font-default font-semibold text-4xl sm:text-5xl lg:text-6xl xl:text-[68px] leading-[1.05] text-foreground tracking-tight max-w-3xl">
            Curated styles,{" "}
            <span className="italic font-normal text-accent">
              designed for
            </span>{" "}
            your modern life.
          </h2>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 4,
        post_type: "product"
      }}>
          <BestsellersConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}